import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import { Copy, Trash2, Plus, Check, Link, MessageCircle } from "lucide-react";
import type { InvitationRole, GroupInvitationResponseDto } from "@tabipla/shared";

// LINE icon component
function LineIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.349 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  );
}

export interface InviteLinkDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Group name for display and share messages */
  groupName?: string;
  /** List of existing invitations */
  invitations?: GroupInvitationResponseDto[];
  /** Loading state for invitations */
  isLoading?: boolean;
  /** Callback to create a new invitation */
  onCreateInvitation: (params: {
    role: InvitationRole;
    expiresInDays: number | null;
  }) => Promise<unknown>;
  /** Whether creation is in progress */
  isCreating?: boolean;
  /** Callback to delete an invitation */
  onDeleteInvitation: (invitationId: string) => Promise<unknown>;
  /** Whether deletion is in progress */
  isDeleting?: boolean;
  /**
   * Template message for copy with message feature.
   * Use {{groupName}} and {{url}} placeholders.
   * @example "「{{groupName}}」に参加してね！\n{{url}}"
   */
  templateMessage?: string;
  /**
   * Template message when no group name is provided.
   * Use {{url}} placeholder.
   */
  templateMessageNoGroup?: string;
  /**
   * LINE share message template.
   * Use {{groupName}} placeholder.
   */
  lineShareMessage?: string;
  /**
   * LINE share message when no group name is provided.
   */
  lineShareMessageNoGroup?: string;
}

export function InviteLinkDialog({
  open,
  onOpenChange,
  groupName,
  invitations,
  isLoading = false,
  onCreateInvitation,
  isCreating = false,
  onDeleteInvitation,
  isDeleting = false,
  templateMessage,
  templateMessageNoGroup,
  lineShareMessage,
  lineShareMessageNoGroup,
}: InviteLinkDialogProps) {
  const { t } = useTranslation();

  const [role, setRole] = useState<InvitationRole>("editor");
  const [expiresInDays, setExpiresInDays] = useState<string>("7");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCreate = async () => {
    await onCreateInvitation({
      role,
      expiresInDays: expiresInDays === "none" ? null : parseInt(expiresInDays, 10),
    });
  };

  const handleDelete = async (invitationId: string) => {
    if (window.confirm(t("invitations.deleteConfirm"))) {
      await onDeleteInvitation(invitationId);
    }
  };

  const handleCopy = async (invitation: GroupInvitationResponseDto) => {
    await navigator.clipboard.writeText(invitation.url);
    setCopiedId(invitation.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyWithMessage = async (invitation: GroupInvitationResponseDto) => {
    // Use custom template or fallback to i18n
    let message: string;
    if (groupName) {
      const template =
        templateMessage ??
        t("invitations.templateMessage", {
          groupName,
          defaultValue: `「${groupName}」に参加してね！\n\n登録不要・30秒で参加できるよ\n${invitation.url}`,
        });
      message = template.replace("{{groupName}}", groupName).replace("{{url}}", invitation.url);
    } else {
      const template =
        templateMessageNoGroup ??
        t("invitations.templateMessageNoGroup", {
          defaultValue: `グループに参加してね！\n\n登録不要・30秒で参加できるよ\n${invitation.url}`,
        });
      message = template.replace("{{url}}", invitation.url);
    }
    await navigator.clipboard.writeText(message);
    setCopiedId(`msg-${invitation.id}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShareToLine = (invitation: GroupInvitationResponseDto) => {
    let message: string;
    if (groupName) {
      message =
        lineShareMessage?.replace("{{groupName}}", groupName) ??
        t("invitations.lineShareMessage", {
          groupName,
          defaultValue: `「${groupName}」に招待されました！下のリンクから参加できます。`,
        });
    } else {
      message =
        lineShareMessageNoGroup ??
        t("invitations.lineShareMessageNoGroup", {
          defaultValue: "グループに招待されました！下のリンクから参加できます。",
        });
    }
    const text = `${message}\n${invitation.url}`;
    const lineUrl = `https://line.me/R/share?text=${encodeURIComponent(text)}`;
    window.open(lineUrl, "_blank");
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t("invitations.noExpiry");
    const date = new Date(dateStr);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            {t("invitations.linkInvite")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Create new invitation */}
          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="text-sm font-medium">{t("invitations.createNew")}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">{t("invitations.role")}</label>
                <Select value={role} onValueChange={(v) => setRole(v as InvitationRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">{t("groups.roles.editor")}</SelectItem>
                    <SelectItem value="viewer">{t("groups.roles.viewer")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">{t("invitations.expiry")}</label>
                <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("invitations.expiryDays", { days: 1 })}</SelectItem>
                    <SelectItem value="7">{t("invitations.expiryDays", { days: 7 })}</SelectItem>
                    <SelectItem value="30">{t("invitations.expiryDays", { days: 30 })}</SelectItem>
                    <SelectItem value="none">{t("invitations.noExpiry")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleCreate} disabled={isCreating} className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              {isCreating ? t("invitations.creating") : t("invitations.createLink")}
            </Button>
          </div>

          {/* Existing invitations */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t("invitations.activeLinks")}</h3>
            {isLoading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t("common.loading")}
              </p>
            ) : !invitations || invitations.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t("invitations.noLinks")}
              </p>
            ) : (
              <div className="space-y-3">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="overflow-hidden rounded-lg border">
                    {/* Link info */}
                    <div className="flex items-center justify-between p-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <code className="truncate rounded bg-muted px-2 py-1 text-xs">
                            {invitation.url}
                          </code>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span
                            className={`rounded-full px-2 py-0.5 ${
                              invitation.role === "editor"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {t(`groups.roles.${invitation.role}`)}
                          </span>
                          <span>{t("invitations.usedCount", { count: invitation.usedCount })}</span>
                          <span>{formatDate(invitation.expiresAt)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(invitation.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    {/* Share actions */}
                    <div className="grid grid-cols-3 gap-2 border-t bg-gray-50/50 p-2 dark:bg-gray-900/50">
                      <Button variant="outline" size="sm" onClick={() => handleCopy(invitation)}>
                        {copiedId === invitation.id ? (
                          <Check className="mr-1.5 h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="mr-1.5 h-4 w-4" />
                        )}
                        <span className="truncate">{t("invitations.copyLink", "コピー")}</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyWithMessage(invitation)}
                      >
                        {copiedId === `msg-${invitation.id}` ? (
                          <Check className="mr-1.5 h-4 w-4 text-green-500" />
                        ) : (
                          <MessageCircle className="mr-1.5 h-4 w-4" />
                        )}
                        <span className="truncate">
                          {t("invitations.copyWithMessage", "文面付き")}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleShareToLine(invitation)}
                        className="bg-[#06C755] text-white hover:bg-[#05a847]"
                      >
                        <LineIcon className="mr-1.5 h-4 w-4" />
                        <span className="truncate">{t("invitations.shareToLine", "LINE")}</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
